import { useCallback, useContext, useEffect, useState } from "react";
import { getSettingByName, Setting, updateSetting } from "util/network/Settings";
import AppStateContext from "contexts/AppStateContext";
import { ColumnType, DataType } from "./PrioritySettingsTable";
import { buildColumnsAndData } from "./TimeTabView";
import { useToast } from "contexts/ToastContext";

const parameters = [
  { parameter: "Base Time", key: "baseTime", category: "Timing" },
  { parameter: "Finish Time Factor", key: "finishTimeFactor", category: "Finish" },
  { parameter: "Finish Price Factor", key: "finishPriceFactor", category: "Finish" },
  { parameter: "Machining Capacity (hours)", key: "machiningCapacity.hours", category: "Machining" },
  { parameter: "Machining Price Factor", key: "machiningPriceFactor", category: "Machining" },
  { parameter: "Material Time Factor", key: "materialTimeFactor", category: "Material" },
  { parameter: "Material Price Factor", key: "materialPriceFactor", category: "Material" },
  { parameter: "Tolerance Price Factor", key: "tolerancePriceFactor", category: "Quality" },
  { parameter: "Surface Quality Price Factor", key: "surfaceQualityPriceFactor", category: "Quality" },
  { parameter: "Material Certificate Price", key: "materialCertificatePrice", category: "Certificates" },
  { parameter: "Food Industry Certificate Price", key: "foodIndustryCertificatePrice", category: "Certificates" },
  { parameter: "Material Certificate Price Factor", key: "materialCertificatePriceFactor", category: "Certificates" },
  { parameter: "Food Industry Certificate Price Factor", key: "foodIndustryCertificatePriceFactor", category: "Certificates" },
];

export const OFFSET_KEYS = [
  "production_deadline_offset_express",
  "production_deadline_offset_standard",
  "production_deadline_offset_economic",
  "production_deadline_offset_custom",
] as const;

export const OFFSET_PARAMETER = "Production Deadline Offset";
export const OFFSET_CATEGORY = "Production Deadline Offset";

/** Maps priority column key (lowercase) to its offset setting name */
const priorityToOffsetKey: Record<string, string> = {
  express: "production_deadline_offset_express",
  standard: "production_deadline_offset_standard",
  economic: "production_deadline_offset_economic",
  custom: "production_deadline_offset_custom",
};

const buildOffsetRow = (
  priorityKeys: string[],
  offsetValues: Record<string, number>,
): DataType => {
  const result: any = {
    parameter: OFFSET_PARAMETER,
    category: OFFSET_CATEGORY,
  };
  priorityKeys.forEach((k) => {
    result[k] = offsetValues[priorityToOffsetKey[k]] ?? 0;
  });
  return result;
};

export const useTimeTab = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [data, setData] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnType[]>([]);
  const [rawSettings, setRawSettings] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const priorityPromise = getSettingByName(token, "pricecalculator_milling_priorityData.json");

    const offsetPromise = Promise.all(
      OFFSET_KEYS.map((key) =>
        getSettingByName(token, key)
          .then((res: Setting) => ({ key, value: Number(res.value) ?? 0 }))
          .catch(() => ({ key, value: 0 })),
      ),
    );

    Promise.all([priorityPromise, offsetPromise])
      .then(([prioritySetting, offsetResults]) => {
        setRawSettings(prioritySetting.value);
        const { cols, rows } = buildColumnsAndData(prioritySetting.value, parameters);
        setColumns(cols);

        const offsetValues = Object.fromEntries(offsetResults.map(({ key, value }) => [key, value]));
        // Priority column keys are the sorted lowercased priority names
        const priorityColKeys = Object.keys(prioritySetting.value).sort(
          (a, b) => (prioritySetting.value[a]?.baseTime ?? 0) - (prioritySetting.value[b]?.baseTime ?? 0),
        ).map((k) => k.toLowerCase());

        const offsetRow = buildOffsetRow(priorityColKeys, offsetValues);
        setData([...rows, offsetRow]);
      })
      .catch((err) => console.error("Failed to fetch settings:", err));
  }, [token]);

  const updateData = useCallback((rowIndex: number, colKey: string, value: number) => {
    setData((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [colKey]: value };
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    if (data.length === 0 || Object.keys(rawSettings).length === 0) return;
    setSaving(true);
    try {
      const payload = structuredClone(rawSettings);
      Object.keys(rawSettings).forEach((key) => {
        const colKey = key.toLowerCase();
        parameters.forEach((param) => {
          const row = data.find((r) => r.parameter === param.parameter);
          if (!row) return;
          const val = row[colKey];
          if (param.key === "machiningCapacity.hours") {
            const hours = Number(val);
            payload[key].machiningCapacity.minutes = hours * 60;
            payload[key].machiningCapacity.hours = hours;
            payload[key].machiningCapacity.seconds = hours * 3600;
          } else if (param.key.includes(".")) {
            const [mainKey, subKey] = param.key.split(".");
            payload[key][mainKey][subKey] = Number(val);
          } else {
            payload[key][param.key] = Number(val);
          }
        });
      });
      await updateSetting(token, "pricecalculator_milling_priorityData.json", payload);

      // Save offset values from the offset row in data
      const offsetRow = data.find((r) => r.parameter === OFFSET_PARAMETER);
      if (offsetRow) {
        await Promise.all(
          Object.entries(priorityToOffsetKey).map(([colKey, settingKey]) =>
            updateSetting(token, settingKey, offsetRow[colKey] ?? 0),
          ),
        );
      }

      addToast({
        type: "success",
        message: "Settings saved successfully",
      });
    } catch (err) {
      console.error("Failed to save settings:", err);
      addToast({
        type: "error",
        message: "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  }, [data, rawSettings, token, addToast]);

  return { data, columns, saving, updateData, save };
};
